import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  CalendarDays,
  Users,
  FileText,
  Search,
  Stethoscope,
  Sparkles,
  Clock,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AiSearchPanel } from "@/components/search/AiSearchPanel";
import {
  getRecentSearches,
  addRecentSearch,
  clearRecentSearches,
} from "@/components/search/useRecentSearches";

type PatientResult = {
  id: string;
  name: string;
  cpf: string | null;
  cell_phone: string | null;
  phone: string | null;
  date_of_birth: string | null;
};

type AppointmentResult = {
  id: string;
  appointment_date: string;
  start_time: string;
  patient_name: string;
  service_name: string | null;
  professional_name: string | null;
};

type SearchMode = "standard" | "ai";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [patients, setPatients] = useState<PatientResult[]>([]);
  const [appointments, setAppointments] = useState<AppointmentResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<SearchMode>("standard");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const navigate = useNavigate();

  // Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Load recent searches and reset on close
  useEffect(() => {
    if (open) {
      setRecentSearches(getRecentSearches());
    } else {
      setMode("standard");
      setQuery("");
      setPatients([]);
      setAppointments([]);
    }
  }, [open]);

  const search = useCallback(async (term: string) => {
    if (term.length < 2) {
      setPatients([]);
      setAppointments([]);
      return;
    }

    setLoading(true);
    const searchTerm = term.toLowerCase();

    try {
      const orClauses = [
        `name_search.ilike.%${searchTerm}%`,
        `cpf.ilike.%${searchTerm.replace(/\D/g, "")}%`,
        `phone.ilike.%${searchTerm.replace(/\D/g, "")}%`,
        `cell_phone.ilike.%${searchTerm.replace(/\D/g, "")}%`,
        `email.ilike.%${searchTerm}%`,
      ].join(",");

      // Parallel: patient search + server-side appointment search (P0.1 + P0.3)
      const [patientRes, appointmentRes] = await Promise.all([
        supabase
          .from("patients")
          .select("id, name, cpf, cell_phone, phone, date_of_birth")
          .or(orClauses)
          .limit(8),
        supabase.rpc("search_appointments", {
          p_term: searchTerm,
        }),
      ]);

      setPatients(patientRes.data ?? []);

      const aptData = (appointmentRes.data ?? []) as Array<{
        id: string;
        appointment_date: string;
        start_time: string;
        patient_id: string;
        patient_name: string;
        service_name: string | null;
        professional_name: string | null;
      }>;

      setAppointments(
        aptData.map((a) => ({
          id: a.id,
          appointment_date: a.appointment_date,
          start_time: a.start_time,
          patient_name: a.patient_name ?? "Paciente",
          service_name: a.service_name,
          professional_name: a.professional_name,
        }))
      );

      // Save recent search
      addRecentSearch(term);
    } catch (err) {
      console.error("Global search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== "standard") return;
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search, mode]);

  // P0.2: Contextual navigation
  const handleSelectPatient = (patientId: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/pacientes?focus=${patientId}`);
  };

  const handleSelectAppointment = (
    appointmentDate: string,
    appointmentId: string
  ) => {
    setOpen(false);
    setQuery("");
    navigate(`/?date=${appointmentDate}&editarAgendamento=${appointmentId}`);
  };

  const handleSelect = (action: () => void) => {
    setOpen(false);
    setQuery("");
    action();
  };

  const handleRecentClick = (term: string) => {
    setQuery(term);
    search(term);
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const showRecentSearches = mode === "standard" && query.length < 2 && recentSearches.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 rounded-lg border bg-background/80 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <Search className="h-3.5 w-3.5" />
        <span>Buscar...</span>
        <kbd className="pointer-events-none ml-2 hidden select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        {/* Mode tabs */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setMode("standard")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors ${
              mode === "standard"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Search className="h-3.5 w-3.5" />
            Busca padrão
          </button>
          <button
            type="button"
            onClick={() => setMode("ai")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors ${
              mode === "ai"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Busca por IA
          </button>
        </div>

        {mode === "ai" ? (
          <AiSearchPanel />
        ) : (
          <>
            <CommandInput
              placeholder="Buscar por nome, CPF, telefone, e-mail..."
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>
                {loading
                  ? "Buscando..."
                  : query.length < 2
                  ? "Digite pelo menos 2 caracteres"
                  : "Nenhum resultado encontrado."}
              </CommandEmpty>

              {/* P1.7: Recent searches */}
              {showRecentSearches && (
                <CommandGroup heading={
                  <span className="flex items-center justify-between">
                    <span>Buscas recentes</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearRecentSearches();
                        setRecentSearches([]);
                      }}
                      className="text-[10px] text-muted-foreground hover:text-foreground"
                    >
                      Limpar
                    </button>
                  </span>
                }>
                  {recentSearches.map((term) => (
                    <CommandItem
                      key={`recent-${term}`}
                      value={`recent-${term}`}
                      onSelect={() => handleRecentClick(term)}
                    >
                      <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                      {term}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {patients.length > 0 && (
                <CommandGroup heading="Pacientes">
                  {patients.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`patient-${p.name}`}
                      onSelect={() => handleSelectPatient(p.id)}
                    >
                      <Users className="mr-2 h-4 w-4 text-primary" />
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {[
                            p.cpf,
                            p.cell_phone || p.phone,
                            p.date_of_birth
                              ? `Nasc: ${p.date_of_birth}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {appointments.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Agendamentos futuros">
                    {appointments.map((a) => (
                      <CommandItem
                        key={a.id}
                        value={`apt-${a.patient_name}-${a.appointment_date}`}
                        onSelect={() =>
                          handleSelectAppointment(a.appointment_date, a.id)
                        }
                      >
                        <CalendarDays className="mr-2 h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">
                            {a.patient_name} —{" "}
                            {formatDate(a.appointment_date)} às{" "}
                            {a.start_time?.slice(0, 5)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {a.service_name ?? "Serviço"}
                            {a.professional_name
                              ? ` · ${a.professional_name}`
                              : ""}
                          </p>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              <CommandSeparator />
              <CommandGroup heading="Navegação rápida">
                <CommandItem
                  onSelect={() => handleSelect(() => navigate("/"))}
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Agenda
                </CommandItem>
                <CommandItem
                  onSelect={() =>
                    handleSelect(() => navigate("/pacientes"))
                  }
                >
                  <Users className="mr-2 h-4 w-4" />
                  Pacientes
                </CommandItem>
                <CommandItem
                  onSelect={() =>
                    handleSelect(() => navigate("/prontuario"))
                  }
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Prontuário
                </CommandItem>
                <CommandItem
                  onSelect={() => handleSelect(() => navigate("/laudos"))}
                >
                  <Stethoscope className="mr-2 h-4 w-4" />
                  Laudos
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </>
        )}
      </CommandDialog>
    </>
  );
}
